/**
 * @file ArchivePOsFlow.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-10-26
 * @description Archiving purchase orders — on their own (Search Purchase
 *              Orders) or with the customer order they were raised for — and
 *              telling the suppliers and hauliers who already have them.
 *
 *              1. A checklist of the POs. Each that can be archived starts
 *                 ticked; one that cannot (billed, synced from Xero) is shown
 *                 greyed with why. A PO its supplier or haulier already holds
 *                 (sent) offers "Email cancellation", on by default; one never
 *                 sent has nobody to tell and offers nothing.
 *              2. The cancellation emails, one after another — the usual send
 *                 modal in its cancellation mode, bannered PDF attached.
 *              3. Only then, the archive — so a PO is never archived while its
 *                 supplier still thinks it is live, unless someone chose that.
 *                 If a cancellation is skipped, the user is asked whether to
 *                 archive anyway rather than it happening silently.
 *
 *              1.0.1 (2026-10-26): a PO made in Xero is labelled PO- (not
 *              PO-D) — it can be archived now, and is deleted in Xero.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import TickCheckbox from './TickCheckbox';
import ConfirmDialog from './ConfirmDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import { useSlideOutButton } from './SlideOutButton';
import { poStatusLabel, poStatusBadgeClass } from '../utils/poStatus';
import './ArchivePOsFlow.css';

const typeLabel = (po) => {
  if (po.po_type === 'haulage' || /^PO-H/i.test(po.purchase_order_number || '')) return 'PO-H';
  const id = String(po.xero_purchase_order_id || '');
  return id && !/^(TEMP-|LOCAL-)/i.test(id) ? 'PO-' : 'PO-D';
};

function ChecklistBody({ intro, note, pos, ticked, notify, toggle, toggleNotify, error, busy }) {
  useSlideOutButton([busy, ticked.size, notify.size]);
  return (
    <div className="archive-pos-body">
      {intro && <p className="archive-pos-intro">{intro}</p>}
      {error && <div className="archive-pos-error">{error}</div>}
      <div className="archive-pos-list">
        {pos.map(po => {
          const blocked = !!po.blocked;
          const isTicked = ticked.has(po.id);
          return (
            <div key={po.id} className={`archive-pos-row${blocked ? ' blocked' : ''}`}>
              <TickCheckbox
                id={`archive-po-${po.id}`}
                checked={isTicked}
                disabled={blocked || busy}
                onChange={() => toggle(po.id)}
                ariaLabel={`Archive ${po.purchase_order_number}`}
              />
              <span className={`archive-pos-type ${typeLabel(po) === 'PO-H' ? 'haulage' : ''}`}>{typeLabel(po)}</span>
              <span className="archive-pos-number">{po.purchase_order_number}</span>
              <span className="archive-pos-supplier" title={po.supplier_name || ''}>{po.supplier_name || '—'}</span>
              <span className={`status-badge ${poStatusBadgeClass(po.status)}`}>{poStatusLabel(po.status)}</span>
              <span className="archive-pos-notify">
                {blocked ? (
                  <span className="archive-pos-kept" title={`Kept — ${po.blocked}`}>kept — {po.blocked}</span>
                ) : po.sent_to_contact ? (
                  <TickCheckbox
                    id={`archive-po-notify-${po.id}`}
                    checked={isTicked && notify.has(po.id)}
                    disabled={!isTicked || busy}
                    onChange={() => toggleNotify(po.id)}
                    label="Email cancellation"
                    ariaLabel={`Email ${po.supplier_name || 'the supplier'} that ${po.purchase_order_number} is cancelled`}
                  />
                ) : (
                  <span className="archive-pos-unsent" title="Never sent — nobody holds it, so there is nobody to tell">not sent</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {note && <p className="archive-pos-note">{note}</p>}
    </div>
  );
}

/**
 * @param {string}   title          Modal title.
 * @param {node}     intro          What is being archived, in words.
 * @param {node}     note           Anything else to know (e.g. driver paperwork already sent).
 * @param {Array}    pos            [{ id, purchase_order_number, xero_purchase_order_id, po_type,
 *                                     status, supplier_name, sent_to_contact, blocked }]
 * @param {function} confirmLabel   (count) => button label.
 * @param {string}   reason         Put in the cancellation email ("customer order cancelled").
 * @param {boolean}  allowNone      The archive can go ahead with no PO ticked (an order on its own).
 * @param {function} onArchive      async (poIds) => void. Throw to show an error and stay open.
 * @param {function} onClose
 * @param {object}   user           For "send as" in the email modal.
 */
function ArchivePOsFlow({ title, intro, note, pos, confirmLabel, reason, allowNone = false, onArchive, onClose, user }) {
  const archivable = useMemo(() => pos.filter(p => !p.blocked), [pos]);
  const [ticked, setTicked] = useState(() => new Set(archivable.map(p => p.id)));
  const [notify, setNotify] = useState(() => new Set(archivable.filter(p => p.sent_to_contact).map(p => p.id)));
  const [stage, setStage] = useState('choose'); // choose | notify | unsent
  const [sentIds, setSentIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const flip = (set, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };
  const toNotify = pos.filter(p => ticked.has(p.id) && p.sent_to_contact && notify.has(p.id));

  const archiveNow = async () => {
    setBusy(true);
    setError('');
    try {
      await onArchive([...ticked]);
    } catch (e) {
      setError(e.message);
      setStage('choose');
    } finally {
      setBusy(false);
    }
  };

  const proceed = () => {
    if (toNotify.length) { setSentIds(new Set()); setStage('notify'); } else archiveNow();
  };

  // Back from the emails: everyone told means archive straight away.
  const missed = toNotify.filter(p => !sentIds.has(p.id));
  useEffect(() => {
    if (stage === 'unsent' && missed.length === 0 && !busy) {
      setStage('choose');
      archiveNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (stage === 'notify') {
    return (
      <SendToSupplierFlow
        linkedPos={toNotify.map(p => ({ ...p, cancellation: { reason } }))}
        orderNumber={null}
        user={user}
        onSent={(message, po) => { if (po?.id) setSentIds(prev => new Set(prev).add(po.id)); }}
        onClose={() => setStage('unsent')}
      />
    );
  }

  if (stage === 'unsent') {
    if (missed.length) {
      return (
        <ConfirmDialog
          isOpen
          title="Archive without telling them?"
          message={`No cancellation was sent for ${missed.map(p => `${p.purchase_order_number} (${p.supplier_name || 'supplier'})`).join(', ')}. `
            + 'Archive anyway? They will still think the order is live until someone tells them.'}
          confirmText="Archive anyway"
          cancelText="Go back"
          onConfirm={() => { setNotify(prev => { const n = new Set(prev); missed.forEach(p => n.delete(p.id)); return n; }); archiveNow(); }}
          onCancel={() => setStage('choose')}
        />
      );
    }
    return null;
  }

  const count = ticked.size;
  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={title}
      size="lg"
      className="archive-pos-flow"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={proceed} disabled={busy || (!count && !allowNone)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">{busy ? 'progress_activity' : 'archive'}</span></div>
              <span className="btn-label">
                {busy ? 'Archiving…' : toNotify.length
                  ? `Email ${toNotify.length} cancellation${toNotify.length === 1 ? '' : 's'}, then archive`
                  : confirmLabel(count)}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <ChecklistBody
        intro={intro}
        note={note}
        pos={pos}
        ticked={ticked}
        notify={notify}
        toggle={(id) => setTicked(prev => flip(prev, id))}
        toggleNotify={(id) => setNotify(prev => flip(prev, id))}
        error={error}
        busy={busy}
      />
    </Modal>
  );
}

export default ArchivePOsFlow;
